import { GetPreviousDataReturn } from '@/dashboards/shared/optimisation/previousData';
import type { OptimizedPlan } from '@/dashboards/shared/optimisation/optimisationSession';
import { PeriodPlan } from '@/optimizer/periodPlan.type';

export type StructureMediaBudgetPeriod = 'daily' | 'monthly' | 'weekly';

export const NO_PERIOD_KEYS = ['category', 'variable', 'total'];
export const isPeriodKey = (key: string) => !NO_PERIOD_KEYS.includes(key);

export type StructureMediaBudgetReturn = {
    category: string;
    variable: string;
    total: number;
} & { [key: number]: number };

type StructureMediaBudgetProps = { optimizedData: OptimizedPlan | undefined } &
    Pick<GetPreviousDataReturn['roiCurves'], 'variablesAndCategories'> & {
        variableNameMap: Record<string, string>;
    };

export const structureMediaBudget = (
    period: StructureMediaBudgetPeriod,
    {
        variablesAndCategories,
        optimizedData,
        variableNameMap,
    }: StructureMediaBudgetProps,
) => {
    const data: StructureMediaBudgetReturn[] = [];

    Object.entries(variablesAndCategories.byCategory).forEach(
        ([categoryName, variables]) => {
            variables.forEach((variable) => {
                const obj: { [key: number]: number } = {};
                let total = 0;

                let planToIterateOn: PeriodPlan[] = [];

                if (period === 'daily') {
                    planToIterateOn = optimizedData?.dailyPlan || [];
                } else if (period === 'monthly') {
                    planToIterateOn = optimizedData?.monthlyPlan || [];
                } else if (period === 'weekly') {
                    planToIterateOn = optimizedData?.weeklyPlan || [];
                }

                planToIterateOn.forEach((plan) => {
                    const key = period === 'daily' 
                        ? String(plan['periodIndex'])
                        : (plan.periodDate || `${period === 'monthly' ? 'Month' : 'Week'} ${plan.periodIndex}`);
                    const value = plan[variable];
                    const numValue = typeof value === 'number' ? value : 0;
                    obj[key] = numValue;
                    total += numValue;
                });
                data.push({
                    category: categoryName,
                    variable: variableNameMap[variable] ?? variable,
                    total,
                    ...obj,
                });
            });
        },
    );

    return data;
};
